import React, { Fragment, useState, useEffect, useMemo } from 'react';
import WebAudio from 'app/services/WebAudio';
import {
  createDeviceManager,
  DeviceManager,
  AUTOMATIC_DEVICE_ID,
  DeviceInfo,
  DeviceManagerState,
  DeviceState,
  DeviceType,
  TrackState,
  UserAction,
  getIsDeviceManagerBusy,
  getDeviceManagerState,
  startDeviceManager,
  stopDeviceManager,
  updateDeviceManager,
} from 'app/services/rtc/DeviceManager';
import { IDevicesPersistedState } from 'app/containers/rtc/DeviceManagerPersistence';
import {
  IDeviceManagerState,
  IDeviceManagerBehaviorOptions,
} from 'app/services/rtc/DeviceManager/DeviceManagerState';
import createServiceContext from 'app/utils/createServiceContext';
import { throwingMethods } from 'app/utils/throwingMethods';
import { subscribableStateAdapter } from 'app/utils/SubscribableState';

const {
  Provider,
  useService: useDeviceManagerService,
  useSelector: useDeviceManagerSelector,
} = createServiceContext<DeviceManager>(subscribableStateAdapter);

export {
  useDeviceManagerService,
  useDeviceManagerSelector,
};

interface IProps {
  webAudio?: WebAudio;
  settings?: IDevicesPersistedState;
  updateSettings?: (state: IDeviceManagerState) => void;
}

export const DeviceManagerProvider: React.FunctionComponent<IProps> = (props) => {
  const { webAudio, settings, updateSettings, children } = props;

  const [manager] = useState(() => {
    const init = DeviceManagerState.init({ webAudio, settings });

    return createDeviceManager(init);
  });

  return (
    <Provider value={manager}>
      <TriggerUpdateSettings updateSettings={updateSettings} />
      {children}
    </Provider>
  );
};

const TriggerUpdateSettings = ({ updateSettings }: Pick<IProps, 'updateSettings'>) => {
  const state = useDeviceManagerState();

  useEffect(() => {
    if (updateSettings) {
      updateSettings(state);
    }
  }, [state, updateSettings]);

  return null;
};

export interface IDeviceManagerActions {
  start: (options: IDeviceManagerBehaviorOptions) => void;
  step: () => void;
  dismiss: () => void;
  reopen: () => void;
  skipAudio: () => void;
  skipVideo: () => void;
  setAudioEnabled: (enabled: boolean) => void;
  setVideoEnabled: (enabled: boolean) => void;
  toggleAudioEnabled: () => void;
  toggleVideoEnabled: () => void;
  toggleScreenSharing: () => void;
  stopScreenSharing: () => void;
  dismissScreenSharingUserAction: () => void;
  setPreferredAudioDeviceId: (id: string) => void;
  setPreferredVideoDeviceId: (id: string) => void;
  setAutoGain: (autoGain: boolean) => void;
  stop: () => void;
}

/**
 * @deprecated
 *
 * For backwards compatibility, re-creates actions object from when
 * DeviceManager was implemented with old createStoreContext (not
 * createServiceContext) abstraction.
 *
 * You can use useDeviceManagerService() and updateDeviceManager() directly instead.
 */
export const useDeviceManager = (): IDeviceManagerActions => {
  const manager = useDeviceManagerService();

  return useMemo((): IDeviceManagerActions => {
    if (!manager) {
      return throwingMethods([
        'start',
        'stop',
        'step',
        'dismiss',
        'reopen',
        'skipAudio',
        'skipVideo',
        'setAudioEnabled',
        'setVideoEnabled',
        'toggleAudioEnabled',
        'toggleVideoEnabled',
        'toggleScreenSharing',
        'stopScreenSharing',
        'dismissScreenSharingUserAction',
        'setPreferredAudioDeviceId',
        'setPreferredVideoDeviceId',
        'setAutoGain',
      ]);
    }

    return {
      start: (options) => startDeviceManager(manager, options),
      stop: () => stopDeviceManager(manager),
      step: () => updateDeviceManager(manager, state => state.step()),
      dismiss: () => updateDeviceManager(manager, state => state.dismiss()),
      reopen: () => updateDeviceManager(manager, state => state.reopen()),
      skipAudio: () => updateDeviceManager(manager, state => state.skipAudio()),
      skipVideo: () => updateDeviceManager(manager, state => state.skipVideo()),
      setAudioEnabled: (enabled: boolean) => updateDeviceManager(manager, state => state.setAudioEnabled(enabled)),
      setVideoEnabled: (enabled: boolean) => updateDeviceManager(manager, state => state.setVideoEnabled(enabled)),
      toggleAudioEnabled: () => updateDeviceManager(manager, state => state.toggleAudioEnabled()),
      toggleVideoEnabled: () => updateDeviceManager(manager, state => state.toggleVideoEnabled()),
      toggleScreenSharing: () => updateDeviceManager(manager, state => state.toggleScreenSharing()),
      stopScreenSharing: () => updateDeviceManager(manager, state => state.stopScreenSharing()),
      dismissScreenSharingUserAction: () =>
        updateDeviceManager(manager, state => state.dismissScreenSharingUserAction()),
      setPreferredAudioDeviceId: (id: string) =>
        updateDeviceManager(manager, state => state.setPreferredAudioDeviceId(id)),
      setPreferredVideoDeviceId: (id: string) =>
        updateDeviceManager(manager, state => state.setPreferredVideoDeviceId(id)),
      setAutoGain: (autoGain: boolean) => updateDeviceManager(manager, state => state.setAutoGain(autoGain)),
    };
  }, [manager]);
};
export const useDeviceManagerState = (): IDeviceManagerState => useDeviceManagerSelector(getDeviceManagerState);
export const useIsDeviceManagerBusy = () => useDeviceManagerSelector(getIsDeviceManagerBusy);

// Render props for backwards compatibility
export const GetDeviceManager    = ({ children }: {
  children: (actions: IDeviceManagerActions) => React.ReactNode,
}) => (
  <Fragment>
    {children(useDeviceManager())}
  </Fragment>
);
export const GetDevices          = ({ children }: {
  children: (state: IDeviceManagerState) => React.ReactNode,
}) => (
  <Fragment>
    {children(useDeviceManagerState())}
  </Fragment>
);
export const IsDeviceManagerBusy = ({ children }: {
  children: (isBusy: boolean) => React.ReactNode,
}) => (
  <Fragment>
    {children(useIsDeviceManagerBusy())}
  </Fragment>
);

export {
  DeviceManagerState,
  DeviceState,
  DeviceType,
  DeviceInfo,
  AUTOMATIC_DEVICE_ID,
  UserAction,
  TrackState,
};
